<template>
  <div v-loading.fullscreen="isSerialLoading" class="flex flex-col responsive-page fixed-table">
    <div class="header-container-wrapper">
      <div class="flex items-center pb-8 border-solid border-b-1 border-gray-300 header-container">
        <div class="flex-grow items-center flex header-container__left">
          <PageHeader :title="$t('Serial')" @load:refreshList="fetchList" />
        </div>
        <div class="flex header-container__right">
          <el-input
            class="w-60"
            prefix-icon="el-icon-search"
            v-model="dataSearch.code"
            @keyup="refreshItemList"
            :placeholder="$t('search_serial_placeholder')"
          />
        </div>
      </div>
    </div>

    <div v-if="getSerials.length > 0">
      <div class="full-page-width flex justify-between table-actions py-5">
        <span class="font-bold text-xl">{{ $t('serial_list') }}</span>
      </div>
      <div class="mb-16 flex-grow">
        <SerialList
          ref="serialListRef"
          :serialData="getSerials"
          @detail="handleDetail"
          @sort-change="handleSortChange"
        />
      </div>
      <div class="justify-center flex flex-row mb-16 paging-container">
        <el-pagination
          layout="prev, pager, next"
          :total="getSerialTotalRecords"
          :page-size="itemsPerPage"
          :current-page="currentPageNum"
          @current-change="currentPageChange"
          background
          class="custom-pagination"
        />
      </div>
    </div>

    <template v-else>
      <ItemNotFound class="mt-56" :content="$t('empty')" />
    </template>
  </div>
</template>

<script lang="ts">
import ItemNotFound from '@/components/common/ItemNotFound.vue'
import PageHeader from '@/components/common/PageHeader.vue'
import SerialList from '@/components/serial/SerialList.vue'
import DataTableMixin from '@/components/mixins/DataTableMixin.vue'
import { FETCH_SERIALS } from '@/store/actions'
import { FILTER_DEBOUNCE_TIME } from '@/utils/constants'
import errorHandler from '@/utils/errorHandler'
import { ISerial } from '@/utils/types/model/serial'
import debounce from 'lodash/debounce'
import { Options } from 'vue-class-component'
import { Mixins } from 'vue-property-decorator'

interface ICommonSearch {
  code?: string
  sortBy?: string
  sortType?: string
  [key: string]: string | undefined
}

@Options({
  components: { PageHeader, ItemNotFound, SerialList },
  name: 'Serial',
})
export default class Serial extends Mixins(DataTableMixin) {
  dataSearch: ICommonSearch = {}
  debounceFilter!: () => void

  get isSerialLoading(): boolean {
    return this.$store.getters.isSerialLoading || false
  }

  get getSerialTotalRecords(): number {
    return this.$store.getters.getSerialTotalRecords || 0
  }

  get getSerials(): ISerial[] {
    return this.$store.getters.getSerials || []
  }

  refreshItemList() {
    this.debounceFilter()
  }

  handleDetail(code: number | string) {
    this.$router.push({ name: 'serial-detail', params: { id: code.toString() } })
  }

  handleSortChange(data: { prop: string; order: string | null }) {
    this.dataSearch.sortBy = data.prop
    this.dataSearch.sortType = data.order ? (data.order === 'ascending' ? 'asc' : 'desc') : undefined
    this.currentPageNum = 1
    this.redirectIndex()
    this.fetchList()
  }

  fetchList() {
    try {
      this.$store.dispatch(FETCH_SERIALS, {
        page: this.currentPageNum,
        limit: this.itemsPerPage,
        code: this.dataSearch.code || '',
        sortBy: this.dataSearch.sortBy,
        sortType: this.dataSearch.sortType,
      })
    } catch (e) {
      errorHandler(e, {}, true)
    }
  }

  created() {
    this.routeName = 'serial'
    this.getQueryData()
    this.debounceFilter = debounce(() => {
      this.currentPageNum = 1
      this.redirectIndex()
      this.fetchList()
    }, FILTER_DEBOUNCE_TIME)
    this.fetchList()
  }

  currentPageChange(pageNum: number) {
    this.currentPageNum = pageNum
    this.redirectIndex()
    this.fetchList()
  }
}
</script>

<style lang="scss" scoped>
@import './src/assets/css/mixins.scss';
.header-container-wrapper {
  background-color: $body-bg;
}

.fixed-table:deep() {
  height: calc(100vh - 100px);
  min-width: 800px;

  &.table-content {
    overflow: auto;
    height: calc(100vh - 350px);
    min-height: 200px;
  }
  &.el-table {
    &::before {
      height: 0px;
    }
    &__header-wrapper {
      position: sticky;
      position: -webkit-sticky;
      top: 0;
      z-index: 10;
    }
  }
}
</style>
